<template>
  <div class="items-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <base-nav
        title="Позиции груза"
        :show-back="true"
        back-fallback="Home"
        :back-fallback-pattern="/\/cargo_items/"
      >
        <base-primary-button
          class="nav-btn"
          @click="toggleCreateForm"
        >
          {{ showCreateForm ? "Отмена" : "Создать" }}
        </base-primary-button>
      </base-nav>

      <transition name="fade-slide">
        <div v-if="showCreateForm" class="card item-form-wrap">
          <h4 class="form-title">Создание позиции груза</h4>
          <CargoItemRegistryForm
            :is-loading="isLoading"
            @save="handleCreate"
            @cancel="showCreateForm = false"
          />
        </div>
      </transition>

      <div v-if="localItems.length === 0" class="no-data">
        Реестр позиций груза пуст
      </div>

      <ul v-else class="items-list">
        <BaseSettingsListItemCard
          v-for="item in localItems"
          :key="item.id"
          :enabled="item.enabled"
          :is-editing="editingItemId === item.id"
          :is-loading="isLoading"
          edit-title="Редактирование позиции груза"
          :show-toggle="true"
          :show-delete="false"
          :created-at="item.created_at"
          :updated-at="item.updated_at"
          @edit="startEdit(item)"
          @toggle="toggleItem(item)"
        >
          <template #edit>
            <CargoItemRegistryForm
              :initial-data="item"
              :is-loading="isLoading"
              @save="(payload) => handleUpdate(item.id, payload)"
              @cancel="editingItemId = null"
            />
          </template>

          <div class="item-top">
            <span class="item-title">
              {{ item.title || "—" }}
            </span>
            <span class="item-code">
              {{ item.code || "—" }}
            </span>
          </div>

          <div class="info-columns desktop-view">
            <div class="info-column">
              <div class="info-section-inner">
                <div class="card-header-inner">
                  <h4 class="card-title">Основное</h4>
                </div>
                <div class="card-content-inner">
                  <div class="info-item">
                    <span class="info-label">Описание:</span>
                    <span class="view-value view-value--description">
                      {{ item.description || "—" }}
                    </span>
                  </div>

                  <div class="info-item">
                    <span class="info-label">Единица измерения:</span>
                    <span class="view-value">
                      {{ formatCargoUnit(item.cargo_unit_id) }}
                    </span>
                  </div>

                  <div class="info-item">
                    <span class="info-label">
                      Базовое количество:
                    </span>
                    <span class="view-value">
                      {{
                        hasValue(item.quantity) ? item.quantity : "—"
                      }}
                    </span>
                  </div>

                  <div class="info-item">
                    <span class="info-label">Вес (кг):</span>
                    <span class="view-value">
                      {{
                        hasValue(item.weight_kg)
                          ? item.weight_kg
                          : "—"
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="info-column">
              <div class="info-section-inner">
                <div class="card-header-inner">
                  <h4 class="card-title">Размеры и объём</h4>
                </div>
                <div class="card-content-inner">
                  <div class="info-item">
                    <span class="info-label">Длина (см):</span>
                    <span class="view-value">
                      {{
                        hasValue(item.length_cm)
                          ? item.length_cm
                          : "—"
                      }}
                    </span>
                  </div>

                  <div class="info-item">
                    <span class="info-label">Ширина (см):</span>
                    <span class="view-value">
                      {{
                        hasValue(item.width_cm) ? item.width_cm : "—"
                      }}
                    </span>
                  </div>

                  <div class="info-item">
                    <span class="info-label">Высота (см):</span>
                    <span class="view-value">
                      {{
                        hasValue(item.height_cm)
                          ? item.height_cm
                          : "—"
                      }}
                    </span>
                  </div>

                  <div class="info-item">
                    <span class="info-label">Объём (м³):</span>
                    <span class="view-value">
                      {{
                        hasValue(item.volume_m3)
                          ? item.volume_m3
                          : "—"
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="compact-view">
            <div class="compact-row">
              <span class="compact-label">Описание:</span>
              <span class="compact-value compact-value--description">
                {{ item.description || "—" }}
              </span>
            </div>

            <div class="compact-row">
              <span class="compact-label">Ед. изм.:</span>
              <span class="compact-value">
                {{ formatCargoUnit(item.cargo_unit_id) }}
              </span>
            </div>

            <div class="compact-row">
              <span class="compact-label">Кол-во:</span>
              <span class="compact-value">
                {{ hasValue(item.quantity) ? item.quantity : "—" }}
              </span>
            </div>

            <div class="compact-row">
              <span class="compact-label">Вес (кг):</span>
              <span class="compact-value">
                {{ hasValue(item.weight_kg) ? item.weight_kg : "—" }}
              </span>
            </div>

            <div class="compact-row">
              <span class="compact-label">Д × Ш × В (см):</span>
              <span class="compact-value">
                {{ formatDimensions(item) }}
              </span>
            </div>

            <div class="compact-row">
              <span class="compact-label">Объём (м³):</span>
              <span class="compact-value">
                {{ hasValue(item.volume_m3) ? item.volume_m3 : "—" }}
              </span>
            </div>
          </div>
        </BaseSettingsListItemCard>
      </ul>

      <pagination-component
        v-if="pagination.pages > 1 && !dataIsLoading"
        :page="pagination.page"
        :pages="pagination.pages"
        :series="pagination.series"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useCargoItemRegistryStore } from "@/stores/cargoItemRegistryStore";
import { useCargoUnitStore } from "@/stores/cargoUnitStore";
import { useNotificationStore } from "@/stores/notificationStore";

import CargoItemRegistryForm from "@/components/Cargo/CargoItemRegistryForm.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";

export default {
  name: "CargoItemRegistriesPage",
  components: {
    CargoItemRegistryForm,
    PaginationComponent,
  },
  data() {
    return {
      dataIsLoading: true,
      showCreateForm: false,
      editingItemId: null,
      localItems: [],
    };
  },
  computed: {
    ...mapState(useCargoItemRegistryStore, [
      "items",
      "isLoading",
      "pagination",
    ]),
    ...mapState(useCargoUnitStore, {
      cargoUnits: "units",
    }),
    notificationStore() {
      return useNotificationStore();
    },
  },
  watch: {
    "$route.query.page": {
      immediate: false,
      handler(newPage) {
        this.loadItems(newPage);
      },
    },
  },
  methods: {
    ...mapActions(useCargoItemRegistryStore, [
      "getItemsRequest",
      "createItem",
      "updateItem",
      "itemEnable",
      "itemDisable",
    ]),
    ...mapActions(useCargoUnitStore, {
      getCargoUnits: "getUnitsRequest",
    }),

    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },

    formatCargoUnit(id) {
      if (!id) return "—";
      const unit = this.cargoUnits.find(
        (u) => String(u.id) === String(id)
      );
      if (!unit) return `#${id}`;
      return unit.registry_short
        ? `${unit.title} (${unit.registry_short})`
        : unit.title;
    },

    formatDimensions(item) {
      const l = this.hasValue(item.length_cm) ? item.length_cm : "—";
      const w = this.hasValue(item.width_cm) ? item.width_cm : "—";
      const h = this.hasValue(item.height_cm) ? item.height_cm : "—";

      if (l === "—" && w === "—" && h === "—") {
        return "—";
      }

      return `${l} × ${w} × ${h}`;
    },

    async loadItems(page) {
      const pageNumber =
        parseInt(page) || parseInt(this.$route.query.page) || 1;

      try {
        this.dataIsLoading = true;
        await Promise.all([
          this.getItemsRequest({ page: pageNumber }),
          this.getCargoUnits(),
        ]);
        this.localItems = (this.items || []).filter(Boolean);
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateLocalItem(updatedItem) {
      const index = this.localItems.findIndex(
        (i) => i && i.id === updatedItem.id
      );

      if (index !== -1) {
        this.localItems.splice(index, 1, updatedItem);
      }
    },

    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm;
      if (this.showCreateForm) {
        this.editingItemId = null;
      }
    },

    startEdit(item) {
      this.editingItemId = item.id;
      this.showCreateForm = false;
    },

    async handleCreate(payload) {
      try {
        await this.createItem(payload);
        this.localItems = (this.items || []).filter(Boolean);
        this.showCreateForm = false;
        this.notificationStore.showSuccess("Позиция груза создана");
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async handleUpdate(id, payload) {
      try {
        await this.updateItem(id, payload);
        this.localItems = (this.items || []).filter(Boolean);
        this.editingItemId = null;
        this.notificationStore.showSuccess("Позиция груза обновлена");
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async toggleItem(item) {
      try {
        if (item.enabled) {
          await this.itemDisable(item.id);
          this.updateLocalItem({ ...item, enabled: false });
          this.notificationStore.showSuccess(
            "Позиция груза отключена"
          );
        } else {
          await this.itemEnable(item.id);
          this.updateLocalItem({ ...item, enabled: true });
          this.notificationStore.showSuccess(
            "Позиция груза включена"
          );
        }
      } catch {
        // ошибка обрабатывается в сторе
      }
    },
  },
  async created() {
    await this.loadItems(this.$route.query.page);
  },
};
</script>

<style scoped>
.items-container {
  flex-grow: 1;
}

.item-form-wrap {
  margin-bottom: var(--base-out-indent);
}

.form-title {
  margin-bottom: var(--base-in-indent);
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
  margin-bottom: 15px;
}

.item-top {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.item-title {
  font-weight: 600;
  font-size: 17px;
}

.item-code {
  color: #666;
}

.desktop-view {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--base-in-indent);
  align-items: start;
}

.info-column {
  display: flex;
  flex-direction: column;
}

.info-section-inner {
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  background: #fff;
}

.card-header-inner {
  padding: var(--base-in-indent);
  background: rgb(240, 240, 240);
  border-bottom: 1px solid #e8e8e8;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-dark);
  margin: 0;
}

.card-content-inner {
  padding: var(--base-in-indent);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 15px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 14px;
  color: #666;
  flex-shrink: 0;
}

.view-value {
  color: var(--color-dark);
  font-weight: 500;
  word-break: break-word;
  text-align: left;
  flex: 1;
  min-width: 0;
}

.view-value--description {
  display: block;
  max-height: 120px;
  overflow-y: auto;
  padding-right: 8px;
  text-align: left;
  white-space: pre-wrap;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.view-value--description:hover {
  scrollbar-color: #ccc transparent;
}

.view-value--description::-webkit-scrollbar {
  width: 4px;
}

.view-value--description::-webkit-scrollbar-track {
  background: transparent;
}

.view-value--description::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 4px;
}

.view-value--description:hover::-webkit-scrollbar-thumb {
  background: #ccc;
}

.compact-view {
  display: none;
  flex-direction: column;
  gap: 8px;
}

.compact-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
}

.compact-label {
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 90px;
}

.compact-value {
  color: var(--color-dark);
  font-weight: 500;
  word-break: break-word;
  text-align: left;
  flex: 1;
  min-width: 0;
}

.compact-value--description {
  display: block;
  max-height: 80px;
  overflow-y: auto;
  padding-right: 8px;
  white-space: pre-wrap;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.compact-value--description:hover {
  scrollbar-color: #ccc transparent;
}

.compact-value--description::-webkit-scrollbar {
  width: 4px;
}

.compact-value--description::-webkit-scrollbar-track {
  background: transparent;
}

.compact-value--description::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 4px;
}

.compact-value--description:hover::-webkit-scrollbar-thumb {
  background: #ccc;
}

@media (max-width: 768px) {
  .desktop-view {
    display: none;
  }

  .compact-view {
    display: flex;
  }

  .item-info {
    width: 100%;
  }

  .compact-label {
    min-width: 82px;
  }

  .compact-value--description {
    max-height: 48px;
  }
}

@media (max-width: 600px) {
  .compact-row {
    gap: 6px;
  }

  .compact-label {
    min-width: 78px;
  }

  .compact-value--description {
    max-height: 42px;
    padding-right: 6px;
  }
}
</style>
